import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export const VERDICT_STYLES = {
  TEST: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25',
  MAYBE: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/25',
  SKIP: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/25',
};

export const VerdictBadge = ({ verdict, className }) => {
  if (!verdict) return null;
  return (
    <span data-testid="verdict-badge" className={cn('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold tracking-wide', VERDICT_STYLES[verdict] || VERDICT_STYLES.MAYBE, className)}>
      {verdict}
    </span>
  );
};

const STAGE_COLORS = {
  neutral: 'bg-muted text-muted-foreground border border-border',
  info: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-500/25',
  warning: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/25',
  success: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25',
  danger: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/25',
};

// Funnel color coding: early (cool violet/indigo/blue) -> build (cyan/teal) ->
// launch/testing (amber/orange) -> winning (lime/vivid emerald) -> dead (muted)
export const STAGE_PILL_STYLES = {
  brain_dump: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border border-slate-400/25',
  product_idea: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-400/30',
  researching: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-400/30',
  qualified: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-400/30',
  store_build: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-400/30',
  creative_prep: 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border border-teal-400/30',
  ready_to_launch: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-400/30',
  live_testing: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-400/30',
  optimizing: 'bg-lime-500/15 text-lime-700 dark:text-lime-300 border border-lime-400/30',
  scaling: 'stage-glow bg-emerald-500/25 text-emerald-700 dark:text-emerald-300 border border-emerald-400/50 font-semibold',
  killed: 'bg-rose-900/25 text-rose-700/80 dark:text-rose-300/60 border border-rose-500/20',
  archived: 'bg-slate-600/15 text-slate-500 dark:text-slate-400/70 border border-slate-500/20',
};

export const StageBadge = ({ stage, stages, className }) => {
  const def = (stages || []).find((s) => s.key === stage);
  const style = STAGE_PILL_STYLES[stage] || STAGE_COLORS[def?.color || 'neutral'];
  return (
    <span data-testid="stage-badge" className={cn('inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium', style, className)}>
      {def?.label || stage}
    </span>
  );
};

export const ScoreRing = ({ score, size = 72, stroke = 6 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 70 ? 'stroke-emerald-500' : pct >= 50 ? 'stroke-amber-500' : 'stroke-rose-500';
  return (
    <div data-testid="product-score-meter" className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-muted" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ - (pct / 100) * circ} className={color} />
      </svg>
      <span data-testid="product-score-value" className="absolute text-lg font-bold tabular-nums font-mono">{score ?? '—'}</span>
    </div>
  );
};

export const KPICard = ({ label, value, sub, tone, testId }) => (
  <Card data-testid={testId} className="glass-data shadow-sm">
    <CardContent className="p-4">
      <p className="kpi-label text-xs font-medium uppercase tracking-wide">{label}</p>
      <p className={cn('mt-1 text-2xl font-semibold tabular-nums font-mono',
        tone === 'good' && 'text-emerald-600 dark:text-emerald-400',
        tone === 'bad' && 'text-rose-600 dark:text-rose-400')}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </CardContent>
  </Card>
);

// Semantic ROAS tone: >=2 winning (green), 1-2 at-risk (amber), <1 losing (red)
export const roasTone = (roas, spend = 1) => {
  if (!spend || roas == null) return '';
  if (roas >= 2) return 'text-emerald-600 dark:text-emerald-400';
  if (roas >= 1) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
};

// Task priority chip styles: P1 red / P2 amber / P3 violet (neutral)
export const PRIORITY_STYLES = {
  1: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
  2: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  3: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
};

export const PriorityChip = ({ priority }) => (
  <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', PRIORITY_STYLES[priority] || PRIORITY_STYLES[3])}>P{priority}</span>
);

export const Spinner = ({ className }) => <Loader2 className={cn('h-4 w-4 animate-spin', className)} />;

export const EmptyState = ({ icon: Icon, title, subtitle, action, testId }) => (
  <div data-testid={testId} className="glass-card flex flex-col items-center justify-center rounded-2xl border-dashed px-6 py-12 text-center">
    {Icon && <Icon className="h-8 w-8 text-muted-foreground/50" />}
    <p className="mt-3 text-sm font-medium">{title}</p>
    {subtitle && <p className="mt-1 max-w-sm text-xs text-muted-foreground">{subtitle}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const SectionTitle = ({ children, right }) => (
  <div className="mb-3 flex items-center justify-between">
    <h2 className="text-sm font-semibold tracking-tight">{children}</h2>
    {right}
  </div>
);

// Lightweight markdown renderer (bold, headers, lists) for AI/SOP text
export const MiniMarkdown = ({ text, className }) => {
  if (!text) return null;
  const lines = String(text).split('\n');
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push({ type: 'ul', items: list }); list = null; } };
  lines.forEach((line) => {
    const t = line.trim();
    if (t.startsWith('- ') || t.startsWith('* ') || /^\d+\.\s/.test(t)) {
      list = list || [];
      list.push(t.replace(/^(-|\*|\d+\.)\s+/, ''));
    } else if (t.startsWith('###')) { flush(); blocks.push({ type: 'h3', text: t.replace(/^#+\s*/, '') }); }
    else if (t.startsWith('##')) { flush(); blocks.push({ type: 'h2', text: t.replace(/^#+\s*/, '') }); }
    else if (t.startsWith('#')) { flush(); blocks.push({ type: 'h2', text: t.replace(/^#+\s*/, '') }); }
    else if (t === '') { flush(); }
    else { flush(); blocks.push({ type: 'p', text: t }); }
  });
  flush();
  const renderInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : p);
  };
  return (
    <div className={cn('prose-app', className)}>
      {blocks.map((b, i) => {
        if (b.type === 'h2') return <h2 key={i}>{renderInline(b.text)}</h2>;
        if (b.type === 'h3') return <h3 key={i}>{renderInline(b.text)}</h3>;
        if (b.type === 'ul') return <ul key={i}>{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
        return <p key={i}>{renderInline(b.text)}</p>;
      })}
    </div>
  );
};
